import { StrictMode } from 'react'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import 'dayjs/locale/nl'
import { QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import { LicenseInfo } from '@mui/x-license'

import { ApiGatewayProvider } from '@dhl/api-gateway-provider'
import { I18nProvider } from '@dhl/i18n'
import { KeycloakAxiosConnector, KeycloakInstanceProvider, KeycloakTokensProvider } from '@dhl/keycloak'
import { configureDhlExtraApi } from '@dhl/receiver-dhl-extra-api'
import { SnackbarProvider } from '@dhl/snackbar'
import { ThemeProvider } from '@dhl-ui/connector'
import { VersionProvider } from '@dhl/version-provider'

import { AppRoutes } from '~/AppRoutes'
import { registerAxiosInterceptors } from '~/common/util/axiosUtil'
import { config, MuiProLicenseKey } from '~/config'
import { getEnv } from '~/getEnv'
import { keyCloakInstance } from '~/keyCloackInstance'
import { queryClient } from '~/query-client'
import { AppLayout } from '~/ui-lib/layout'

const { apiGatewayHost, basePath, version } = config

registerAxiosInterceptors()

const basePathWithoutSlash = basePath.slice(0, -1)

configureDhlExtraApi({
  baseUrl: '/api/loyalty',
})

LicenseInfo.setLicenseKey(MuiProLicenseKey)

export const App = () => {
  const isE2eTest = getEnv() === 'e2e'
  return (
    <StrictMode>
      <ApiGatewayProvider host={apiGatewayHost}>
        <ThemeProvider>
          <QueryClientProvider client={queryClient}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nl">
              <KeycloakInstanceProvider instance={keyCloakInstance}>
                <KeycloakTokensProvider initOptions={isE2eTest ? { checkLoginIframe: false } : undefined}>
                  <KeycloakAxiosConnector>
                    <SnackbarProvider>
                      <I18nProvider>
                        <BrowserRouter
                          basename={basePathWithoutSlash}
                          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
                        >
                          <VersionProvider url={`${basePath}version.json`} version={version}>
                            <AppLayout>
                              <AppRoutes />
                            </AppLayout>
                          </VersionProvider>
                        </BrowserRouter>
                      </I18nProvider>
                    </SnackbarProvider>
                  </KeycloakAxiosConnector>
                </KeycloakTokensProvider>
              </KeycloakInstanceProvider>
            </LocalizationProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </ApiGatewayProvider>
    </StrictMode>
  )
}
